import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import FDCDataSummary from '../components/fdcDataSummary';
import MobileFDCDataSummary from '../components/mobileFDCDataSummary';
import Modal from '../components/modal';
import LoadingAnimation from '../components/loadingAnimation';
import '../styles/FDCSubmissions.css';
import '../styles/mobile/FDCSubmissions.css';
import { 
  StatusCellRenderer, 
  NetworkCellRenderer, 
  EntityCellRenderer,
  FDCAttestationStatusRenderer
} from '../utils/metrics';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useStatus } from '../context/StatusContext';
import { getApiBase } from '../utils/apiBase';
import { useDataUpdates } from '../hooks/useDataUpdates';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';
import { StatusEvaluator } from '../context/StatusEvaluator';

const formatFDCData = (data) => {
  const formattedData = [];
  let maxRound = 0;
  
  Object.entries(data).forEach(([entity, networks]) => {
    Object.entries(networks).forEach(([network, rounds]) => {
      Object.entries(rounds).forEach(([roundId, roundData]) => {
        const roundNum = parseInt(roundId, 10);
        maxRound = Math.max(maxRound, roundNum);
        
        // Count attestations and valid attestations
        // Handle new structure where roundData contains attestations array
        let attestations = [];
        if (Array.isArray(roundData)) {
          // Old structure: roundData is directly an array of attestations
          attestations = roundData;
        } else if (roundData && roundData.attestations && Array.isArray(roundData.attestations)) {
          // New structure: roundData.attestations contains the array
          attestations = roundData.attestations;
        }
        
        const attestationCount = attestations.length;
        const validCount = attestations.filter(att => att.bitvote_result === true).length;
        
        // Extract submitSignatures status from the new data structure
        let submitSignaturesStatus = 'MISSED';
        let submitSignaturesTxLink = null;
        
        // Check the new data structure for submitSignatures
        if (roundData && typeof roundData === 'object' && !Array.isArray(roundData)) {
          // New structure: roundData contains both attestations array and submitSignatures object
          if (roundData.submitSignatures) {
            const submitSigData = roundData.submitSignatures;
            submitSignaturesStatus = submitSigData.status || 'MISSED';
            submitSignaturesTxLink = submitSigData.tx_link || null;
          }
        }
        
        // Use actual timestamp from the data instead of calculating
        let roundEndTime = null;
        if (roundData && typeof roundData === 'object' && !Array.isArray(roundData)) {
          // Check if timestamp exists in the round data
          roundEndTime = roundData.timestamp || null;
        }

        formattedData.push({
          voting_round_id: roundNum,
          entity,
          network,
          round_end: roundEndTime,
          attestations: attestationCount,
          valid: validCount,
          attestation_details: attestations,
          submitSignatures: submitSignaturesStatus,
          submitSignatures_tx_link: submitSignaturesTxLink
        });
      });
    });
  });
          
  // Sort and filter to last 6 hours (240 rounds)
  const cutoffRound = maxRound - 240;
  return formattedData
    .filter(row => row.voting_round_id >= cutoffRound)
    .sort((a, b) => b.voting_round_id - a.voting_round_id);
};

function FDCSubmissions() {
  const [rowData, setRowData] = useState([]);
  const [summaryDataAll, setSummaryDataAll] = useState({});
  const [fdcDataAll, setFdcDataAll] = useState({});
  const [loading, setLoading] = useState(true);
  const { updatePageStatus } = useStatus();
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: null
  });
  const isInitialLoadRef = useRef(true);
  const { getCachedData } = useDataCache();
  
  // Mobile state management
  const [isMobile, setIsMobile] = useState(false);
  const [currentView, setCurrentView] = useState('summary'); // 'summary' or 'submissions'
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Summary configurations for pagination dots
  const summaryConfigs = [
    { entity: 'UYS', network: 'flare', title: 'UYS Flare' },
    { entity: 'UYS', network: 'songbird', title: 'UYS Songbird' },
    { entity: 'ENV', network: 'flare', title: 'Envision Flare' },
    { entity: 'ENV', network: 'songbird', title: 'Envision Songbird' }
  ];

  const fdcMapRef = useRef(new Map());
  const API = getApiBase();

  const handleDataUpdate = useCallback(async () => {
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    console.log('FDC handleDataUpdate: consuming cache if available');
    try {
      // Warm-start from cache immediately (fast paint)
      const cachedSummary = getCachedData('/fdc_submission_summary.json');
      if (cachedSummary) {
        setSummaryDataAll(cachedSummary);
        try {
          const overallPageStatus = StatusEvaluator.fdcSubmissions(cachedSummary, {});
          updatePageStatus('fdcSubmissions', overallPageStatus);
        } catch {}
      }
    } catch {}

    // On SSE update events, explicitly refresh summary so the UI updates in real time.
    try {
      await cacheManager.updateEndpoints(['/fdc_submission_summary.json']);
      const freshSummary = getCachedData('/fdc_submission_summary.json');
      if (freshSummary) {
        setSummaryDataAll(freshSummary);
        const overallPageStatus = StatusEvaluator.fdcSubmissions(freshSummary, {});
        updatePageStatus('fdcSubmissions', overallPageStatus);
      }
    } catch (error) {
      console.error('Error in FDC handleDataUpdate:', error);
      // Do not force red here; keep last known status to avoid flicker on transient errors
    } finally {
      setLoading(false);
    }
  }, [updatePageStatus, getCachedData]);
  
  // SSE: listen for global updates for summary, and fdc_delta adds-only for grid
  const handleFdcDelta = useCallback((data) => {
    try {
      if (!data || data.type !== 'fdc_delta') return;
      const m = fdcMapRef.current || new Map();
      if (Array.isArray(data.adds)) {
        data.adds.forEach(r => { if (r && r.id) m.set(r.id, r); });
      }
      // enforce cutoff window
      const cut = data.cutoff || {};
      if (cut && (cut.flare || cut.songbird)) {
        for (const [id, row] of Array.from(m.entries())) {
          const co = cut[row.network] || 0;
          if (Number(row.voting_round_id) < co) m.delete(id);
        }
      }
      fdcMapRef.current = m;
      const arr = Array.from(m.values()).sort((a,b) => b.voting_round_id - a.voting_round_id);
      setRowData(arr);
    } catch {}
  }, []);

  useDataUpdates(handleDataUpdate, { onFdcDelta: handleFdcDelta });
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    console.log('FDC Component mounted, performing initial data fetch');
    cacheManager.registerEndpoints('fdcSubmissions', [
      '/fdc_submission_summary.json'
    ]);

    try {
      const cachedSummary = getCachedData('/fdc_submission_summary.json');
      if (cachedSummary) {
        setSummaryDataAll(cachedSummary);
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    } catch {}
    (async () => {
      try {
        const resp = await fetch(`${API}/api/fdc/snapshot?window=240`);
        if (resp.ok) {
          const snap = await resp.json();
          if (snap && Array.isArray(snap.rows)) {
            const m = new Map();
            snap.rows.forEach(r => { if (r && r.id) m.set(r.id, r); });
            fdcMapRef.current = m;
            const arr = Array.from(m.values()).sort((a,b) => b.voting_round_id - a.voting_round_id);
            setRowData(arr);
          } else {
          }
        } else {
        }
      } catch {}
      await handleDataUpdate();
      setLoading(false);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        console.log('FDC initial snapshot complete');
      }
    })();
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      console.log('FDC Initial load process complete, isInitialLoadRef set to false.');
    }
    
    return () => {
      console.log('FDC Component unmounting, cleaning up state');
      setRowData([]);
      setSummaryDataAll({});
      setFdcDataAll({});
      isInitialLoadRef.current = true;
      cacheManager.unregisterEndpoints('fdcSubmissions');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [handleDataUpdate]);

  const handleMissingRoundsClick = (entity, network, timeframe, missingRounds) => {
    console.log('Opening modal with:', { entity, network, timeframe, missingRounds });
    setModalState(prevState => {
      const newState = {
        isOpen: true,
        title: `Missing FDC Submissions - ${entity} ${network} (${timeframe})`,
        content: (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Voting rounds with missing FDC submissions:
            </p>
            <div className="grid grid-cols-4 gap-2">
              {missingRounds.map(round => (
                <div key={round} className="bg-gray-100 rounded p-2 text-center text-black">
                  {round}
                </div>
              ))}
            </div>
          </div>
        )
      };
      console.log('New FDC modal state:', newState);
      return newState;
    });
  };

  const columnDefs = [
    { 
      field: 'voting_round_id', 
      headerName: 'Round', 
      minWidth: 120, 
      maxWidth: 130, 
      flex: 1.2, 
      sortable: true, 
      valueParser: params => Number(params.newValue),
    },
    { 
      field: 'entity', 
      headerName: 'Entity', 
      sortable: true, 
      filter: true, 
      minWidth: 120, 
      maxWidth: 140, 
      flex: 1.2, 
      cellRenderer: EntityCellRenderer 
    },
    { 
      field: 'network', 
      headerName: 'Network', 
      cellRenderer: NetworkCellRenderer, 
      minWidth: 140, 
      maxWidth: 200, 
      flex: 1.3 
    },
    { 
      field: 'round_end', 
      headerName: 'Round End', 
      sortable: true, 
      filter: true, 
      minWidth: 160, 
      maxWidth: 180, 
      flex: 1.5, 
      sort: 'desc', 
      sortingOrder: ['desc', 'asc'],
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'UTC'
        }).replace(',', '');
      }
    },
    { 
      field: 'submitSignatures', 
      headerName: 'Signatures', 
      sortable: true, 
      filter: true, 
      minWidth: 100,
      maxWidth: 180,
      flex: 1,
      cellRenderer: (params) => {
        const status = params.value;
        const txLink = params.data?.submitSignatures_tx_link;
        
        // Determine status class
        let statusClass = '';
        switch (status?.toUpperCase()) {
          case 'SENT':
            statusClass = 'status-success';
            break;
          case 'SENT (LATE)':
            statusClass = 'status-late';
            break;
          case 'PENDING':
            statusClass = 'status-pending';
            break;
          case 'FAILED':
          case 'MISSED':
          default:
            statusClass = 'status-missing';
        }
        
        // Create the status cell content
        const statusElement = React.createElement('div', {
          className: `status-cell ${statusClass}`
        }, status);
        
        // Wrap in the proper wrapper structure
        const wrappedContent = React.createElement('div', {
          className: 'status-cell-wrapper'
        }, txLink && status !== 'MISSED' ? 
          React.createElement('a', {
            href: txLink,
            target: '_blank',
            rel: 'noopener noreferrer',
            style: { textDecoration: 'none' },
            onClick: (e) => e.stopPropagation()
          }, statusElement) : statusElement
        );
        
        return wrappedContent;
      }
    },
    { 
      field: 'attestations', 
      headerName: 'Attestations', 
      sortable: true, 
      filter: true, 
      minWidth: 60,
      maxWidth: 160,
      flex: 1,
      valueFormatter: params => {
        const total = params.value || 0;
        const valid = params.data?.valid || 0;
        return `${valid}/${total}`;
      }
    },
    { 
      field: 'attestation_details', 
      headerName: 'Status', 
      sortable: false, 
      filter: false, 
      minWidth: 200, 
      flex: 2,
      cellRenderer: FDCAttestationStatusRenderer,
      valueFormatter: params => {
        // Return a string representation for AG Grid, but the cellRenderer will handle the actual display
        const attestations = params.value || [];
        return `${attestations.length} attestations`;
      }
    }
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
  };

  // Mobile-optimized column definitions with pinned columns
  const mobileColumnDefs = [
    { 
      field: 'voting_round_id', 
      headerName: 'VR', 
      width: 69,
      pinned: 'left',
      sortable: true, 
      filter: true,
      valueParser: params => Number(params.newValue),
    },
    { 
      field: 'entity', 
      headerName: 'Entity', 
      width: 85,
      pinned: 'left',
      sortable: true, 
      filter: false, 
      cellRenderer: EntityCellRenderer 
    },
    { 
      field: 'network', 
      headerName: 'Network', 
      width: 106,
      cellRenderer: NetworkCellRenderer, 
    },
    { 
      field: 'round_end', 
      headerName: 'End Time', 
      sortable: true, 
      filter: false, 
      width: 130,
      sort: 'desc', 
      sortingOrder: ['desc', 'asc'],
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'UTC'
        }).replace(',', '');
      }
    },
    { 
      field: 'attestations', 
      headerName: 'Att', 
      sortable: false, 
      filter: false, 
      width: 70,
      valueFormatter: params => {
        const total = params.value || 0;
        const valid = params.data?.valid || 0;
        return `${valid}/${total}`;
      }
    },
    { 
      field: 'submitSignatures', 
      headerName: 'Signatures', 
      sortable: true, 
      filter: false, 
      width: 120,
      cellRenderer: (params) => {
        const status = params.value;
        const txLink = params.data?.submitSignatures_tx_link;
        
        let statusClass = '';
        switch (status?.toUpperCase()) {
          case 'SENT':
            statusClass = 'status-success';
            break;
          case 'SENT (LATE)':
            statusClass = 'status-late';
            break;
          case 'FAILED':
          case 'MISSED':
          default:
            statusClass = 'status-missing';
        }
        
        const statusElement = React.createElement('div', {
          className: `status-cell ${statusClass}`
        }, status);
        
        const wrappedContent = React.createElement('div', {
          className: 'status-cell-wrapper'
        }, txLink && status !== 'MISSED' ? 
          React.createElement('a', {
            href: txLink,
            target: '_blank',
            rel: 'noopener noreferrer',
            style: { textDecoration: 'none' },
            onClick: (e) => e.stopPropagation()
          }, statusElement) : statusElement
        );
        
        return wrappedContent;
      }
    },    
    { 
      field: 'attestation_details', 
      headerName: 'Status', 
      sortable: false, 
      filter: false, 
      width: 230,
      cellRenderer: FDCAttestationStatusRenderer,
      valueFormatter: params => {
        const attestations = params.value || [];
        return `${attestations.length} attestations`;
      }
    },


  ];

  const getRowId = useCallback((params) => {
    return `${params.data.entity}-${params.data.network}-${params.data.voting_round_id}`;
  }, []);

  if (loading) {
    return (
      <div className={isMobile ? "mobile-wrapper fdc-page" : "main-wrapper"}>
        <div className="loading-content">
          <LoadingAnimation />
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="mobile-wrapper fdc-page">
        <div className="mobile-fdc-overview">
          {/* Toggle between Summary and Submissions */}
          <div className="view-toggle-container">
            <span className={`toggle-label ${currentView === 'summary' ? 'active' : ''}`}>
              Summary
            </span>
            <div className="toggle-switch">
              <input 
                type="checkbox" 
                id="fdc-view-toggle" 
                checked={currentView === 'submissions'}
                onChange={() => {
                  const newView = currentView === 'summary' ? 'submissions' : 'summary';
                  setCurrentView(newView);
                  setCurrentSlide(0);
                }}
              />
              <label htmlFor="fdc-view-toggle" />
            </div>
            <span className={`toggle-label ${currentView === 'submissions' ? 'active' : ''}`}>
              Submissions
            </span>
          </div>

          {currentView === 'summary' ? (
            <>
              <div className="fdc-summary-view">
                <MobileFDCDataSummary
                  data={summaryDataAll}
                  fdcDataAll={fdcDataAll}
                  onMissingRoundsClick={handleMissingRoundsClick}
                  currentSlide={currentSlide}
                  onSlideChange={setCurrentSlide}
                />
              </div>
              
              <div className="pagination-dots-container">
                {Array.from({ length: summaryConfigs.length }).map((_, index) => (
                  <div 
                    key={index} 
                    className={`dot ${index === currentSlide ? 'active' : ''}`}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="fdc-submissions-view">
              <div className="fdc-mobile-grid-wrapper">
                <div className="ag-theme-alpine-dark custom-dark-theme" style={{ height: '100%', width: '100%' }}>
                  <AgGridReact
                    columnDefs={mobileColumnDefs}
                    rowData={rowData}
                    defaultColDef={{
                      sortable: true,
                      resizable: false
                    }}
                    gridOptions={{
                      suppressScrollOnNewData: true,
                      suppressHorizontalScroll: false,
                      domLayout: 'normal',
                      getRowHeight: () => 50,
                      suppressColumnVirtualisation: true
                    }}
                    getRowId={getRowId}
                    domLayout="normal"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="absolute inset-0" style={{ position: 'fixed', zIndex: 9999 }}>
          <Modal
            isOpen={modalState.isOpen}
            onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
            title={modalState.title}
          >
            {modalState.content}
          </Modal>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="main-wrapper">
      <FDCDataSummary 
        data={summaryDataAll}
        fdcDataAll={fdcDataAll}
        onMissingRoundsClick={handleMissingRoundsClick}
      />
      <div className="grid-wrapper">
        <div className="ag-theme-alpine-dark custom-dark-theme">
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            suppressHorizontalScroll={false}
            rowHeight={45}
            headerHeight={50}
            getRowId={getRowId}
            pagination={true}
            paginationPageSize={100}
            paginationNumberFormatter={function(params) {
              return '[' + params.value.toLocaleString() + ']';
            }}
            paginationAutoPageSize={false}
          />
        </div>
      </div>
  
      <div className="absolute inset-0" style={{ position: 'fixed', zIndex: 9999 }}>
        <Modal
          isOpen={modalState.isOpen}
          onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
          title={modalState.title}
        >
          {modalState.content}
        </Modal>
      </div>
    </div>
  );
}

export default FDCSubmissions; 